import { useMemo, useState } from "react";

// ---------------- Types & data ----------------

type AgeGroup = "child" | "teen" | "adult" | "senior";
type Sex = "male" | "female" | "na";

type IndicationKey =
  | "ibs_c"
  | "ibs_d"
  | "ibs_m"
  | "post_abx"
  | "aad"
  | "general"
  | "immune"
  | "vaginal"
  | "gut_brain"
  | "skin"
  | "pediatric"
  | "teen_wellness"
  | "healthy_aging";

type HistoryKey =
  | "on_abx"
  | "recent_abx"
  | "sibo"
  | "ibd"
  | "immunocompromised"
  | "pregnant"
  | "yeast"
  | "hospitalization"
  | "none";

type SymptomKey =
  | "bloating"
  | "constipation"
  | "diarrhea"
  | "cramping"
  | "gas"
  | "brain_fog"
  | "infections"
  | "skin";

interface IndicationContent {
  label: string;
  evidence: string;
  strains: string[];
  cfu: string;
  cfuRange: string;
  duration: string;
  url: string;
}

const INDICATIONS: Record<IndicationKey, IndicationContent> = {
  post_abx: {
    label: "Post-Antibiotic Recovery",
    evidence:
      "Antibiotic-associated diarrhea affects up to 30% of patients on antibiotic therapy. The strongest evidence supports Lactobacillus rhamnosus GG and Saccharomyces boulardii for prevention. S. boulardii is particularly valuable during an active antibiotic course as it is a yeast and survives antibiotic exposure.",
    strains: [
      "Lactobacillus rhamnosus GG",
      "Saccharomyces boulardii CNCM I-745",
      "Bifidobacterium longum",
    ],
    cfu: "20–50 billion CFU/day. Refrigerated formulations preferred. Take 2 hours apart from antibiotic dose. Continue 2 weeks post-course.",
    cfuRange: "20–50 billion CFU/day",
    duration: "Through course + 2 weeks after",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMTQ=",
  },
  aad: {
    label: "Antibiotic-Associated Diarrhea Prevention",
    evidence:
      "Antibiotic-associated diarrhea affects up to 30% of patients on antibiotic therapy. The strongest evidence supports Lactobacillus rhamnosus GG and Saccharomyces boulardii for prevention. S. boulardii is particularly valuable during an active antibiotic course as it is a yeast and survives antibiotic exposure.",
    strains: [
      "Lactobacillus rhamnosus GG",
      "Saccharomyces boulardii CNCM I-745",
      "Bifidobacterium longum",
    ],
    cfu: "20–50 billion CFU/day. Refrigerated formulations preferred. Take 2 hours apart from antibiotic dose. Continue 2 weeks post-course.",
    cfuRange: "20–50 billion CFU/day",
    duration: "Through course + 2 weeks after",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMTQ=",
  },
  ibs_d: {
    label: "IBS — Diarrhea Predominant",
    evidence:
      "Probiotics show modest but meaningful benefit for IBS-D symptoms including urgency and loose stools. Strain specificity matters significantly — pooled analyses that ignore strain differences underestimate efficacy. Allow a minimum of 8 weeks to assess response.",
    strains: ["Lactobacillus plantarum 299V", "Bifidobacterium infantis 35624"],
    cfu: "10–20 billion CFU/day. Shelf-stable formulations appropriate. Take consistently at the same time daily.",
    cfuRange: "10–20 billion CFU/day",
    duration: "Minimum 8 weeks",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMTY=",
  },
  ibs_c: {
    label: "IBS — Constipation Predominant",
    evidence:
      "Bifidobacterium-dominant formulas show the most consistent benefit for IBS-C, supporting motility and intestinal barrier function. Evidence is promising but most trials are short-term. Pairing with prebiotic fiber may enhance efficacy.",
    strains: [
      "Bifidobacterium longum",
      "Bifidobacterium lactis",
      "Lactobacillus acidophilus",
    ],
    cfu: "10–20 billion CFU/day. Shelf-stable appropriate. Take with or without food consistently.",
    cfuRange: "10–20 billion CFU/day",
    duration: "8–12 weeks",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMTc=",
  },
  ibs_m: {
    label: "IBS — Mixed",
    evidence:
      "IBS-M requires a broader multi-strain approach to address both motility and stool consistency. Evidence for mixed-type IBS is more limited than for IBS-D or IBS-C individually, but multi-strain formulas show the most consistent benefit across symptom types.",
    strains: [
      "Lactobacillus plantarum 299V",
      "Bifidobacterium infantis 35624",
      "Saccharomyces boulardii",
    ],
    cfu: "10–20 billion CFU/day. Shelf-stable appropriate. Minimum 8 weeks before assessing response.",
    cfuRange: "10–20 billion CFU/day",
    duration: "Minimum 8 weeks",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMTg=",
  },
  vaginal: {
    label: "Vaginal & Urogenital Health",
    evidence:
      "A healthy vaginal microbiome is dominated by Lactobacillus species. The dual-strain combination of L. reuteri RC-14 and L. rhamnosus GR-1 has the strongest clinical evidence for vaginal microbiome restoration and recurrent BV prevention among oral probiotic formulations.",
    strains: [
      "Lactobacillus reuteri RC-14",
      "Lactobacillus rhamnosus GR-1",
      "Lactobacillus crispatus",
    ],
    cfu: "1–10 billion CFU/day orally. Refrigerated formulations preferred. Duration 1–4 months depending on indication.",
    cfuRange: "1–10 billion CFU/day",
    duration: "1–4 months",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMTk=",
  },
  general: {
    label: "General Gut Health & Maintenance",
    evidence:
      "For general digestive wellness without a specific diagnosis, broad multi-strain formulas combining Lactobacillus and Bifidobacterium species support gut barrier integrity, microbiome diversity, and digestive regularity. Consistency of daily use is the most important factor.",
    strains: ["Multi-strain Lactobacillus + Bifidobacterium combination"],
    cfu: "5–20 billion CFU/day. Shelf-stable or refrigerated. Same time daily for best results.",
    cfuRange: "5–20 billion CFU/day",
    duration: "Ongoing daily use",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMjA=",
  },
  immune: {
    label: "Immune Support",
    evidence:
      "The gut houses approximately 70% of immune tissue, making microbiome support a key lever for immune resilience. Bifidobacterium lactis Bl-04 has the strongest RCT evidence specifically for upper respiratory infection prevention and immune modulation.",
    strains: [
      "Bifidobacterium lactis Bl-04",
      "Lactobacillus rhamnosus GG",
      "Lactobacillus acidophilus NCFM",
    ],
    cfu: "10–20 billion CFU/day. Shelf-stable or refrigerated. Particularly valuable during high-stress periods or seasonal immune challenges.",
    cfuRange: "10–20 billion CFU/day",
    duration: "8–12 weeks or seasonal",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMjE=",
  },
  gut_brain: {
    label: "Gut-Brain Axis & Mood Support",
    evidence:
      "The gut-brain connection is bidirectional — microbiome composition influences neurotransmitter production, vagal nerve signaling, and systemic inflammation. The psychobiotic combination of L. helveticus R0052 and B. longum R0175 has RCT evidence for reducing anxiety and depressive symptoms. Benefits emerge more slowly than digestive ones — allow 8+ weeks.",
    strains: [
      "Lactobacillus helveticus R0052",
      "Bifidobacterium longum R0175",
    ],
    cfu: "10–20 billion CFU/day. Shelf-stable or refrigerated. Combine with adequate dietary fiber and omega-3 intake for enhanced gut-brain response.",
    cfuRange: "10–20 billion CFU/day",
    duration: "Minimum 8 weeks",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xODkwNDI=",
  },
  skin: {
    label: "Eczema & Skin Health",
    evidence:
      "The gut-skin axis connects intestinal barrier function and systemic inflammation to skin conditions including eczema and rosacea. Lactobacillus rhamnosus GG has the most evidence for atopic dermatitis. Skin responses are slower than digestive ones — allow 8–12 weeks minimum before assessing response.",
    strains: [
      "Lactobacillus rhamnosus GG",
      "Bifidobacterium lactis",
      "Lactobacillus salivarius",
    ],
    cfu: "5–20 billion CFU/day. Shelf-stable or refrigerated. Works best alongside dietary anti-inflammatory support.",
    cfuRange: "5–20 billion CFU/day",
    duration: "8–12 weeks minimum",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMjU=",
  },
  pediatric: {
    label: "Pediatric Digestive Health",
    evidence:
      "Lactobacillus rhamnosus GG is the most studied pediatric probiotic strain with robust evidence for antibiotic-associated diarrhea and acute gastroenteritis. Saccharomyces boulardii is a strong complement during antibiotic courses as it survives antibiotic exposure. Always confirm dosing with the child's pediatrician.",
    strains: ["Lactobacillus rhamnosus GG", "Saccharomyces boulardii"],
    cfu: "5–10 billion CFU/day. Chewable or powder formulations preferred. Refrigerated where feasible.",
    cfuRange: "5–10 billion CFU/day",
    duration: "4–8 weeks or through antibiotic course",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMjQ=",
  },
  teen_wellness: {
    label: "Teen General Wellness",
    evidence:
      "Adolescence involves significant microbiome shifts driven by hormonal changes, stress, diet, and frequent antibiotic exposure. A broad multi-strain formula supports digestive regularity, immune function, mood, and skin — all common concerns in this age group. If the teen recently completed an antibiotic course, the post-antibiotic protocol is recommended first.",
    strains: ["Multi-strain Lactobacillus + Bifidobacterium combination"],
    cfu: "5–15 billion CFU/day. Shelf-stable preferred for compliance. Daily consistent use.",
    cfuRange: "5–15 billion CFU/day",
    duration: "Ongoing daily use",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMjc=",
  },
  healthy_aging: {
    label: "Healthy Aging (65+)",
    evidence:
      "Microbiome diversity naturally declines with age, contributing to immune senescence, digestive slowing, and increased inflammation. Bifidobacterium species are most depleted with aging. Higher potency formulations may be needed compared to younger adults due to reduced colonization efficiency.",
    strains: [
      "Bifidobacterium longum",
      "Bifidobacterium lactis",
      "Lactobacillus acidophilus",
    ],
    cfu: "10–20 billion CFU/day. Refrigerated preferred. Ongoing daily use.",
    cfuRange: "10–20 billion CFU/day",
    duration: "Ongoing daily use",
    url: "https://us.fullscript.com/s/drnykin/shop/plans/TXVsdGlQYXRpZW50UGxhbi0xNzIzMjY=",
  },
};

const PRIMARY_REASONS: { key: IndicationKey; label: string; desc: string }[] = [
  { key: "ibs_c", label: "IBS — Constipation Predominant", desc: "IBS-C: infrequent or hard stools." },
  { key: "ibs_d", label: "IBS — Diarrhea Predominant", desc: "IBS-D: urgency or loose stools." },
  { key: "ibs_m", label: "IBS — Mixed", desc: "IBS-M: alternating constipation and diarrhea." },
  { key: "post_abx", label: "Post-Antibiotic Gut Recovery", desc: "Rebuild after a recent antibiotic course." },
  { key: "aad", label: "Antibiotic-Associated Diarrhea", desc: "Currently on antibiotics, want prevention." },
  { key: "general", label: "General Gut Health & Maintenance", desc: "Daily microbiome support." },
  { key: "immune", label: "Immune Support", desc: "Respiratory and seasonal resilience." },
  { key: "vaginal", label: "Vaginal & Urogenital Health", desc: "Lactobacillus-based microbiome support." },
  { key: "gut_brain", label: "Gut-Brain Axis & Mood Support", desc: "Psychobiotic strains for mood." },
  { key: "skin", label: "Eczema & Skin Health", desc: "Gut-skin axis support." },
  { key: "pediatric", label: "Child Digestive Health", desc: "Pediatric-appropriate strains." },
];

const HISTORY_OPTIONS: { key: HistoryKey; label: string }[] = [
  { key: "on_abx", label: "Currently taking antibiotics" },
  { key: "recent_abx", label: "Finished antibiotics within the last 4 weeks" },
  { key: "sibo", label: "History of SIBO" },
  { key: "ibd", label: "Diagnosed IBD (Crohn's or ulcerative colitis)" },
  { key: "immunocompromised", label: "Immunocompromised or on immunosuppressant medication" },
  { key: "pregnant", label: "Pregnant or breastfeeding" },
  { key: "yeast", label: "Recurrent yeast infections (3+ per year)" },
  { key: "hospitalization", label: "Recent hospitalization or surgery (within 3 months)" },
  { key: "none", label: "None of the above" },
];

const SYMPTOMS: { key: SymptomKey; label: string }[] = [
  { key: "bloating", label: "Bloating" },
  { key: "constipation", label: "Constipation" },
  { key: "diarrhea", label: "Loose stools or diarrhea" },
  { key: "cramping", label: "Abdominal cramping or pain" },
  { key: "gas", label: "Excessive gas" },
  { key: "brain_fog", label: "Brain fog or fatigue" },
  { key: "infections", label: "Frequent colds or infections" },
  { key: "skin", label: "Skin flares (eczema, rosacea)" },
];

// ---------------- Component ----------------

type Step = 1 | 2 | 3 | 4 | 5 | 6; // 5 = red flag; 6 = results

export default function App() {
  const [step, setStep] = useState<Step>(1);
  const [age, setAge] = useState<AgeGroup | null>(null);
  const [sex, setSex] = useState<Sex | null>(null);
  const [reason, setReason] = useState<IndicationKey | null>(null);
  const [history, setHistory] = useState<HistoryKey[]>([]);
  const [symptoms, setSymptoms] = useState<SymptomKey[]>([]);
  const [acknowledgedFlag, setAcknowledgedFlag] = useState(false);

  const reasons = useMemo(() => {
    if (!age) return PRIMARY_REASONS;
    return PRIMARY_REASONS.filter((r) => {
      if (r.key === "pediatric" && age !== "child") return false;
      if (r.key === "vaginal" && sex !== "female") return false;
      if (age === "child" && ["ibs_c", "ibs_d", "ibs_m", "gut_brain", "vaginal"].includes(r.key)) return false;
      return true;
    });
  }, [age, sex]);

  const redFlags = useMemo(() => {
    const flags: { title: string; message: string }[] = [];
    if (history.includes("immunocompromised"))
      flags.push({
        title: "Immune system consideration",
        message:
          "Because your immune system may be compromised, probiotic safety depends heavily on strain and form. Please consult your clinician before starting any probiotic.",
      });
    if (history.includes("ibd"))
      flags.push({
        title: "IBD consideration",
        message:
          "Probiotic use in IBD requires careful strain selection and should be guided by your provider.",
      });
    if (history.includes("sibo") && reason && ["ibs_c", "ibs_d", "ibs_m"].includes(reason))
      flags.push({
        title: "SIBO consideration",
        message:
          "Probiotics may worsen symptoms in some patients with SIBO. We recommend discussing this with your provider before starting.",
      });
    if (history.includes("pregnant"))
      flags.push({
        title: "Pregnancy / breastfeeding consideration",
        message:
          "Probiotics are generally considered safe in pregnancy, but strain selection matters. Please confirm with your OB or midwife before starting.",
      });
    if (history.includes("hospitalization"))
      flags.push({
        title: "Recent hospitalization",
        message:
          "Post-surgical gut health requires individualized assessment. Please consult your care team before supplementing.",
      });
    return flags;
  }, [history, reason]);

  const indicationKey: IndicationKey | null = useMemo(() => {
    if (!reason) return null;
    if (history.includes("on_abx")) return "aad";
    if (history.includes("recent_abx") && reason === "general") return "post_abx";
    if (reason === "general" && age === "senior") return "healthy_aging";
    if (reason === "general" && age === "teen") return "teen_wellness";
    if (age === "child") return "pediatric";
    return reason;
  }, [reason, history, age]);

  const progress = step <= 4 ? (step / 4) * 100 : 100;

  function toggleHistory(k: HistoryKey) {
    setHistory((prev) => {
      if (k === "none") return prev.includes("none") ? [] : ["none"];
      const without = prev.filter((x) => x !== "none");
      return without.includes(k) ? without.filter((x) => x !== k) : [...without, k];
    });
  }
  function toggleSymptom(k: SymptomKey) {
    setSymptoms((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }

  function goNext() {
    if (step === 1 && age && sex) setStep(2);
    else if (step === 2 && reason) setStep(3);
    else if (step === 3) setStep(4);
    else if (step === 4) {
      if (redFlags.length > 0 && !acknowledgedFlag) setStep(5);
      else setStep(6);
    } else if (step === 5) {
      setAcknowledgedFlag(true);
      setStep(6);
    }
  }
  function goBack() {
    if (step === 1) return;
    setStep((s) => (s - 1) as Step);
  }
  function restart() {
    setStep(1);
    setAge(null);
    setSex(null);
    setReason(null);
    setHistory([]);
    setSymptoms([]);
    setAcknowledgedFlag(false);
  }

  // suppress unused warning — symptoms collected for future use
  void symptoms;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:py-12">
        <Header />

        {step <= 4 && (
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <span>Step {step} of 4</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <main className="mt-8">
          {step === 1 && (
            <StepShell title="Who is this for?" subtitle="Select an age group and sex to continue.">
              <div className="grid gap-6 sm:grid-cols-2">
                <FieldGroup label="Age group">
                  {[
                    { v: "child", l: "Child (2–12)" },
                    { v: "teen", l: "Teen (13–17)" },
                    { v: "adult", l: "Adult (18–64)" },
                    { v: "senior", l: "65+" },
                  ].map((o) => (
                    <OptionRow
                      key={o.v}
                      selected={age === o.v}
                      onClick={() => setAge(o.v as AgeGroup)}
                      label={o.l}
                    />
                  ))}
                </FieldGroup>
                <FieldGroup label="Sex">
                  {[
                    { v: "male", l: "Male" },
                    { v: "female", l: "Female" },
                    { v: "na", l: "Prefer not to say" },
                  ].map((o) => (
                    <OptionRow
                      key={o.v}
                      selected={sex === o.v}
                      onClick={() => setSex(o.v as Sex)}
                      label={o.l}
                    />
                  ))}
                </FieldGroup>
              </div>
            </StepShell>
          )}

          {step === 2 && (
            <StepShell title="What is the primary reason?" subtitle="Choose the option closest to your main goal.">
              <div className="grid gap-3 sm:grid-cols-2">
                {reasons.map((r) => (
                  <CardOption
                    key={r.key}
                    selected={reason === r.key}
                    onClick={() => setReason(r.key)}
                    title={r.label}
                    desc={r.desc}
                  />
                ))}
              </div>
            </StepShell>
          )}

          {step === 3 && (
            <StepShell title="Health history" subtitle="Select all that apply.">
              <div className="grid gap-2">
                {HISTORY_OPTIONS.map((h) => (
                  <CheckRow
                    key={h.key}
                    label={h.label}
                    checked={history.includes(h.key)}
                    onChange={() => toggleHistory(h.key)}
                  />
                ))}
              </div>
            </StepShell>
          )}

          {step === 4 && (
            <StepShell
              title="Current symptoms"
              subtitle="Optional — helps refine your results."
            >
              <div className="grid gap-2 sm:grid-cols-2">
                {SYMPTOMS.map((s) => (
                  <CheckRow
                    key={s.key}
                    label={s.label}
                    checked={symptoms.includes(s.key)}
                    onChange={() => toggleSymptom(s.key)}
                  />
                ))}
              </div>
            </StepShell>
          )}

          {step === 5 && <RedFlagGate flags={redFlags} onContinue={goNext} />}

          {step === 6 && indicationKey && (
            <Results
              indication={INDICATIONS[indicationKey]}
              onAbx={history.includes("on_abx") || history.includes("recent_abx")}
              onRestart={restart}
            />
          )}

          {step <= 4 && (
            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                onClick={goBack}
                disabled={step === 1}
                className="rounded-2xl px-5 py-3 text-sm font-medium text-muted-foreground transition hover:text-foreground disabled:opacity-40"
              >
                ← Back
              </button>
              <button
                onClick={goNext}
                disabled={
                  (step === 1 && (!age || !sex)) ||
                  (step === 2 && !reason)
                }
                className="rounded-2xl bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                {step === 4 ? "See my recommendations" : "Continue"}
              </button>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </div>
  );
}

// ---------------- Sub-components ----------------

function Header() {
  return (
    <header className="text-center">
      <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium uppercase tracking-wider text-accent">
        <span className="h-1.5 w-1.5 rounded-full bg-accent" />
        Personalized Probiotic Guide
      </div>
      <h1 className="mt-4 text-3xl font-normal text-primary sm:text-4xl">
        Probiotic Recommendation Tool
      </h1>
      <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
        {"Evidence-informed strain, CFU, and protocol guidance\u00a0"}
      </p>
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-12 border-t border-border pt-6 text-center">
      <p className="mx-auto max-w-2xl text-xs leading-relaxed text-muted-foreground">
        This tool provides general educational guidance and does not constitute medical advice.
        Individual needs vary. Please consult a qualified healthcare provider before starting any
        supplement, particularly if you have a diagnosed medical condition or are pregnant.{"\u00a0"}
      </p>
      <p className="mx-auto mt-4 max-w-2xl text-xs leading-relaxed text-muted-foreground">
        © 2026 Pacha Wellness LLC. All rights reserved. Created by Linda Nykin, ND CFMP.
      </p>
    </footer>
  );
}

function StepShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-6 shadow-sm sm:p-8">
      <h2 className="text-2xl font-normal text-primary">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-sm font-bold uppercase tracking-wider text-foreground">
        {label}
      </div>
      <div className="grid gap-2">{children}</div>
    </div>
  );
}

function OptionRow({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left text-sm transition ${
        selected
          ? "border-primary bg-primary text-primary-foreground shadow-sm"
          : "border-border bg-surface hover:border-accent hover:bg-secondary"
      }`}
    >
      <span className="font-medium">{label}</span>
      <span
        className={`ml-3 inline-block h-4 w-4 rounded-full border-2 ${
          selected ? "border-primary-foreground bg-primary-foreground" : "border-border"
        }`}
      />
    </button>
  );
}

function CardOption({
  title,
  desc,
  selected,
  onClick,
}: {
  title: string;
  desc: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border p-4 text-left transition ${
        selected
          ? "border-primary bg-primary text-primary-foreground shadow-sm"
          : "border-border bg-surface hover:border-accent hover:bg-secondary"
      }`}
    >
      <div className="text-sm font-semibold">{title}</div>
      <div
        className={`mt-1 text-xs leading-relaxed ${
          selected ? "text-primary-foreground/80" : "text-muted-foreground"
        }`}
      >
        {desc}
      </div>
    </button>
  );
}

function CheckRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition ${
        checked ? "border-accent bg-secondary" : "border-border bg-surface hover:border-accent"
      }`}
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition ${
          checked ? "border-accent bg-accent text-accent-foreground" : "border-border bg-surface"
        }`}
      >
        {checked && (
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
            <path
              fillRule="evenodd"
              d="M16.7 5.3a1 1 0 010 1.4l-7.4 7.4a1 1 0 01-1.4 0L3.3 9.5a1 1 0 011.4-1.4L8.6 12l6.7-6.7a1 1 0 011.4 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <span className="font-medium text-foreground">{label}</span>
    </label>
  );
}

function RedFlagGate({
  flags,
  onContinue,
}: {
  flags: { title: string; message: string }[];
  onContinue: () => void;
}) {
  return (
    <section className="rounded-2xl border border-warm bg-warm/40 p-6 shadow-sm sm:p-8">
      <div className="inline-flex items-center gap-2 rounded-full bg-warm-foreground/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-warm-foreground">
        Before we continue
      </div>
      <h2 className="mt-3 text-2xl font-normal text-primary">
        A few clinical notes for your situation
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Based on what you shared, please review the following before relying on general guidance.
      </p>

      <div className="mt-5 space-y-3">
        {flags.map((f, i) => (
          <div
            key={i}
            className="rounded-2xl border border-border bg-surface p-4"
            style={{ borderLeft: "4px solid var(--color-accent)" }}
          >
            <div className="text-sm font-semibold text-primary">{f.title}</div>
            <p className="mt-1 text-sm leading-relaxed text-foreground/80">{f.message}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button
          onClick={onContinue}
          className="rounded-2xl border border-primary bg-surface px-5 py-3 text-sm font-semibold text-primary transition hover:bg-secondary"
        >
          Continue to General Guidance
        </button>
        <a
          href="https://www.pachaintegrativemed.com"
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-2xl bg-primary px-5 py-3 text-center text-sm font-semibold text-primary-foreground transition hover:bg-primary-hover"
        >
          Consult with Dr. Nykin →
        </a>
      </div>
    </section>
  );
}

function Results({
  indication,
  onAbx,
  onRestart,
}: {
  indication: IndicationContent;
  onAbx: boolean;
  onRestart: () => void;
}) {
  const abxLine = onAbx
    ? " Take at least 2 hours apart from your antibiotic dose. Continue for 2 weeks after finishing your course."
    : "";

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-xs font-semibold uppercase tracking-wider text-accent">
          Your personalized guidance
        </div>
        <h2 className="mt-2 text-3xl font-normal text-primary">{indication.label}</h2>
      </div>

      <ResultCard tone="primary">
        <div className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/80">
          Recommended Products
        </div>
        <h3 className="mt-2 font-serif text-2xl text-primary-foreground">
          {indication.label} Protocol
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-primary-foreground/90">
          Key strains:{" "}
          {indication.strains.slice(0, 3).map((s, i) => (
            <span key={s}>
              {i > 0 && " · "}
              <em>{s}</em>
            </span>
          ))}
        </p>
        <p className="mt-1 text-sm leading-relaxed text-primary-foreground/90">
          Target CFU: {indication.cfuRange} · Duration: {indication.duration}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-primary-foreground/80">
          Third-party tested products selected for confirmed strain identity and clinical CFU
          potency.
        </p>
        <a
          href={indication.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center justify-center rounded-2xl bg-surface px-6 py-3 text-sm font-semibold text-primary shadow-sm transition hover:bg-secondary"
        >
          Shop This Probiotic Protocol →
        </a>
      </ResultCard>

      <ResultCard>
        <SectionLabel>What the Evidence Says</SectionLabel>
        <p className="mt-2 text-sm leading-relaxed text-foreground/85">{indication.evidence}</p>
      </ResultCard>

      <ResultCard>
        <SectionLabel>Strains to Look For</SectionLabel>
        <ul className="mt-3 space-y-2">
          {indication.strains.map((s) => (
            <li key={s} className="flex items-start gap-2 text-sm text-foreground/85">
              <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
              <span className="font-medium italic">{s}</span>
            </li>
          ))}
        </ul>
      </ResultCard>

      <ResultCard>
        <SectionLabel>CFU Range & Timing</SectionLabel>
        <p className="mt-2 text-sm leading-relaxed text-foreground/85">
          {indication.cfu}
          {abxLine}
        </p>
      </ResultCard>

      <div className="pt-2 text-center">
        <button
          onClick={onRestart}
          className="text-sm font-medium text-muted-foreground underline-offset-4 transition hover:text-primary hover:underline"
        >
          ← Start over
        </button>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wider text-accent">{children}</div>
  );
}

function ResultCard({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "primary";
}) {
  if (tone === "primary") {
    return (
      <div className="rounded-2xl bg-primary p-6 shadow-md sm:p-7">{children}</div>
    );
  }
  return (
    <div
      className="rounded-2xl border border-border bg-surface p-6 shadow-sm sm:p-7"
      style={{ borderLeft: "4px solid var(--color-primary)" }}
    >
      {children}
    </div>
  );
}
